import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import Icon from '../../../components/AppIcon';
import occupationUpdateService from '../../../services/occupationUpdateService';
import blsApiService from '../../../services/blsApiService';
import onetApiService from '../../../services/onetApiService';

const OccupationUpdates = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [occupationsNeedingUpdate, setOccupationsNeedingUpdate] = useState([]);
  const [updateResults, setUpdateResults] = useState(null);
  const [blsStatus, setBlsStatus] = useState(null);
  const [onetStatus, setOnetStatus] = useState(null);
  const [updateMethod, setUpdateMethod] = useState('comprehensive');

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadOccupationsNeedingUpdate();
      checkBlsStatus();
      checkOnetStatus();
    }
  }, [isAdmin]);

  const loadOccupationsNeedingUpdate = async () => {
    try {
      setLoading(true);
      const result = await occupationUpdateService.getOccupationsNeedingUpdate(30);
      
      if (result.success) {
        setOccupationsNeedingUpdate(result.data || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load occupations needing update');
    } finally {
      setLoading(false);
    }
  };

  const checkBlsStatus = async () => {
    try {
      const result = await blsApiService.getPopularSeries();
      setBlsStatus(result.success ? 'connected' : 'error');
    } catch (err) {
      setBlsStatus('error');
    }
  };

  const checkOnetStatus = async () => {
    try {
      const result = await onetApiService.getOccupationCategories();
      setOnetStatus(result.success ? 'connected' : 'error');
    } catch (err) {
      setOnetStatus('error');
    }
  };

  const handleUpdateAll = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const result = await occupationUpdateService.scheduleAutomaticUpdates();
      
      if (result.success) {
        setSuccess(`Successfully processed ${result.results?.length || 0} occupation updates`);
        setUpdateResults(result);
        loadOccupationsNeedingUpdate();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update occupations');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSingle = async (occupationId, socCode) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      let result;
      switch (updateMethod) {
        case 'onet':
          result = await occupationUpdateService.updateOccupationFromOnet(occupationId, socCode);
          break;
        case 'bls':
          result = await occupationUpdateService.updateOccupationFromBls(occupationId, socCode);
          break;
        case 'comprehensive':
        default:
          result = await occupationUpdateService.updateOccupationComprehensive(occupationId, socCode);
          break;
      }
      
      if (result.success) {
        setSuccess(`Successfully updated occupation ${occupationId} using ${updateMethod} method`);
        loadOccupationsNeedingUpdate();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update occupation');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <Card>
            <CardContent className="p-8 text-center">
              <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                You need admin privileges to access this page.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Occupation Updates - CareerLift Admin</title>
        <meta name="description" content="Manage occupation data updates from BLS and O*NET APIs" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Occupation Data Updates
            </h1>
            <p className="text-muted-foreground">
              Manage occupation data updates from the Bureau of Labor Statistics (BLS) and O*NET APIs
            </p>
          </div>

          {/* API Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Icon name="Database" size={20} />
                  <span>BLS API Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <Badge variant={blsStatus === 'connected' ? 'default' : 'destructive'}>
                    {blsStatus === 'connected' ? 'Connected' : 'Error'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {blsStatus === 'connected' 
                      ? 'BLS API is accessible'
                      : 'Unable to connect to BLS API'
                    }
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Icon name="Briefcase" size={20} />
                  <span>O*NET API Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <Badge variant={onetStatus === 'connected' ? 'default' : 'destructive'}>
                    {onetStatus === 'connected' ? 'Connected' : 'Error'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {onetStatus === 'connected' 
                      ? 'O*NET API is accessible'
                      : 'Unable to connect to O*NET API'
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Error Display */}
          {error && (
            <Card className="mb-6 border-destructive">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2 text-destructive">
                  <Icon name="AlertTriangle" size={20} />
                  <span className="font-medium">Error:</span>
                  <span>{error}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success Display */}
          {success && (
            <Card className="mb-6 border-green-500">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2 text-green-600">
                  <Icon name="CheckCircle" size={20} />
                  <span className="font-medium">Success:</span>
                  <span>{success}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Update Controls */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Update Controls</CardTitle>
              <CardDescription>
                Update occupation data from the latest BLS and O*NET statistics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium">Update Method:</label>
                  <select
                    value={updateMethod}
                    onChange={(e) => setUpdateMethod(e.target.value)}
                    className="px-3 py-2 border border-input rounded-md bg-background"
                  >
                    <option value="comprehensive">Comprehensive (O*NET + BLS)</option>
                    <option value="onet">O*NET Only</option>
                    <option value="bls">BLS Only</option>
                  </select>
                </div>

                <div className="flex items-center space-x-4">
                  <Button
                    onClick={handleUpdateAll}
                    disabled={loading || occupationsNeedingUpdate.length === 0}
                    className="flex items-center space-x-2"
                  >
                    <Icon name="RefreshCw" size={16} className={loading ? 'animate-spin' : ''} />
                    <span>
                      Update All ({occupationsNeedingUpdate.length} occupations)
                    </span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={loadOccupationsNeedingUpdate}
                    disabled={loading}
                  >
                    <Icon name="RefreshCw" size={16} />
                    Refresh List
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Occupations Needing Update */}
          <Card>
            <CardHeader>
              <CardTitle>Occupations Needing Update</CardTitle>
              <CardDescription>
                Occupations that haven't been updated in the last 30 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader2" size={24} className="animate-spin" />
                  <span className="ml-2">Loading...</span>
                </div>
              ) : occupationsNeedingUpdate.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="CheckCircle" size={48} className="mx-auto mb-4" />
                  <p>All occupations are up to date!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {occupationsNeedingUpdate.map((occupation) => (
                    <div
                      key={occupation.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium">{occupation.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          SOC Code: {occupation.soc_code}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Last Updated: {occupation.bls_last_updated 
                            ? new Date(occupation.bls_last_updated).toLocaleDateString()
                            : 'Never'
                          }
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleUpdateSingle(occupation.id, occupation.soc_code)}
                        disabled={loading}
                      >
                        Update
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Update Results */}
          {updateResults && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Update Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p><strong>Total Processed:</strong> {updateResults.summary?.total || 0}</p>
                  <p><strong>Successful:</strong> {updateResults.summary?.successful || 0}</p>
                  <p><strong>Failed:</strong> {updateResults.summary?.failed || 0}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default OccupationUpdates;